<template>
    <div class="mt-6 rounded-lg border border-gray-200 bg-white">
        <div class="flex items-center justify-between border-b px-5 py-4">
            <div>
                <h3 class="font-semibold text-gray-900">
                    Asset Locations
                </h3>

                <p class="text-sm text-gray-500">
                    Manage asset locations for this Lite Integration.
                </p>
            </div>

            <button
                type="button"
                class="rounded-md bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700"
                @click="openAddForm"
            >
                Add Asset Location
            </button>
        </div>

        <div
            v-if="error"
            class="m-5 rounded-md bg-red-50 p-3 text-sm text-red-700"
        >
            {{ error }}
        </div>

        <div
            v-if="loading"
            class="p-6 text-center text-sm text-gray-500"
        >
            Loading asset locations...
        </div>

        <div
            v-else-if="!assetLocations.length"
            class="p-6 text-center text-sm text-gray-500"
        >
            No asset locations have been added.
        </div>

        <div
            v-else
            class="divide-y divide-gray-200"
        >
            <div
                v-for="relationship in assetLocations"
                :key="relationship.uuid"
            >
                <div class="flex items-center justify-between gap-4 px-5 py-4">
                    <div class="flex min-w-0 items-center gap-4">
                        <img
                            v-if="
                                relationship.asset_location
                                && relationship.asset_location.image_url
                            "
                            :src="relationship.asset_location.image_url"
                            :alt="relationship.asset_location.name"
                            class="h-12 w-12 rounded-md object-cover"
                        >

                        <div class="min-w-0">
                            <div class="font-medium text-gray-900">
                                {{
                                    relationship.asset_location
                                        ? relationship.asset_location.name
                                        : 'Unknown asset location'
                                }}
                            </div>

                            <div
                                v-if="
                                    relationship.asset_location
                                    && relationship.asset_location.experience
                                "
                                class="text-sm text-gray-500"
                            >
                                {{
                                    getExperienceName(
                                        relationship
                                            .asset_location
                                            .experience
                                    )
                                }}
                            </div>
                        </div>
                    </div>

                    <button
                        type="button"
                        class="rounded-md border border-red-300 px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50"
                        :disabled="
                            deletingUuid === relationship.uuid
                        "
                        @click="removeAssetLocation(relationship)"
                    >
                        {{
                            deletingUuid === relationship.uuid
                                ? 'Removing...'
                                : 'Remove'
                        }}
                    </button>
                </div>

                <div class="px-5 pb-5">
                    <LiteIntegrationAssetLocationAssets
                        :lite-integration="liteIntegration"
                        :lite-integration-asset-location="relationship"
                        :experience-assets-revision="experienceAssetsRevision"
                    />
                </div>
            </div>
        </div>

        <div
            v-if="addFormVisible"
            class="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
            @click.self="closeAddForm"
        >
            <div
                class="w-full max-w-2xl rounded-lg bg-white shadow-xl"
            >
                <div
                    class="flex items-center justify-between border-b px-6 py-4"
                >
                    <div>
                        <h3 class="text-lg font-semibold">
                            Add Asset Location
                        </h3>

                        <p class="text-sm text-gray-500">
                            Only locations belonging to attached
                            experiences are shown.
                        </p>
                    </div>

                    <button
                        type="button"
                        class="text-2xl leading-none text-gray-400 hover:text-gray-600"
                        @click="closeAddForm"
                    >
                        &times;
                    </button>
                </div>

                <div class="p-6">
                    <div
                        v-if="formError"
                        class="mb-4 rounded-md bg-red-50 p-3 text-sm text-red-700"
                    >
                        {{ formError }}
                    </div>

                    <div
                        v-if="loadingAvailable"
                        class="py-8 text-center text-sm text-gray-500"
                    >
                        Loading available asset locations...
                    </div>

                    <div
                        v-else-if="!availableAssetLocations.length"
                        class="rounded-md border border-gray-200 p-6 text-center text-sm text-gray-500"
                    >
                        No additional asset locations are available.
                    </div>

                    <div
                        v-else
                        class="max-h-[60vh] divide-y divide-gray-200 overflow-y-auto rounded-md border border-gray-200"
                    >
                        <button
                            v-for="assetLocation in availableAssetLocations"
                            :key="assetLocation.uuid"
                            type="button"
                            class="flex w-full items-center justify-between gap-4 p-4 text-left hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                            :disabled="
                                savingUuid === assetLocation.uuid
                            "
                            @click="addAssetLocation(assetLocation)"
                        >
                            <div
                                class="flex min-w-0 items-center gap-4"
                            >
                                <img
                                    v-if="assetLocation.image_url"
                                    :src="assetLocation.image_url"
                                    :alt="assetLocation.name"
                                    class="h-12 w-12 rounded-md object-cover"
                                >

                                <div class="min-w-0">
                                    <div
                                        class="font-medium text-gray-900"
                                    >
                                        {{ assetLocation.name }}
                                    </div>

                                    <div
                                        v-if="assetLocation.experience"
                                        class="text-sm text-gray-500"
                                    >
                                        {{
                                            getExperienceName(
                                                assetLocation.experience
                                            )
                                        }}
                                    </div>
                                </div>
                            </div>

                            <span
                                class="text-sm font-medium text-blue-600"
                            >
                                {{
                                    savingUuid === assetLocation.uuid
                                        ? 'Adding...'
                                        : 'Add'
                                }}
                            </span>
                        </button>
                    </div>
                </div>

                <div
                    class="flex justify-end border-t px-6 py-4"
                >
                    <button
                        type="button"
                        class="rounded-md border border-gray-300 px-4 py-2 text-sm hover:bg-gray-50 disabled:opacity-50"
                        :disabled="Boolean(savingUuid)"
                        @click="closeAddForm"
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import LiteIntegrationAssetLocationAssets
    from '@/components/LiteIntegrationAssetLocationAssets.vue';

export default {
    name: 'LiteIntegrationAssetLocations',

    components: {
        LiteIntegrationAssetLocationAssets,
    },

    props: {
        liteIntegration: {
            type: Object,
            required: true,
        },
        experienceAssetsRevision: {
            type: Number,
            required: true,
        },
    },

    data() {
        return {
            assetLocations: [],
            availableAssetLocations: [],
            loading: false,
            loadingAvailable: false,
            savingUuid: null,
            deletingUuid: null,
            addFormVisible: false,
            error: null,
            formError: null,
        };
    },

    mounted() {
        this.loadAssetLocations();
    },

    methods: {
        async loadAssetLocations() {
            this.loading = true;
            this.error = null;

            try {
                const response = await this.$http.get(
                    `/lite-integrations/${this.liteIntegration.uuid}/asset-locations`
                );

                this.assetLocations =
                    response.data.data ?? [];
            } catch (error) {
                this.error = this.getErrorMessage(
                    error,
                    'Unable to load asset locations.'
                );
            } finally {
                this.loading = false;
            }
        },

        async loadAvailableAssetLocations() {
            this.loadingAvailable = true;
            this.formError = null;

            try {
                const response = await this.$http.get(
                    `/lite-integrations/${this.liteIntegration.uuid}/asset-locations/available`
                );

                this.availableAssetLocations =
                    response.data.data ?? [];
            } catch (error) {
                this.formError = this.getErrorMessage(
                    error,
                    'Unable to load available asset locations.'
                );
            } finally {
                this.loadingAvailable = false;
            }
        },

        async openAddForm() {
            this.addFormVisible = true;
            this.availableAssetLocations = [];
            this.formError = null;

            await this.loadAvailableAssetLocations();
        },

        closeAddForm() {
            if (this.savingUuid) {
                return;
            }

            this.addFormVisible = false;
            this.availableAssetLocations = [];
            this.formError = null;
        },

        async addAssetLocation(assetLocation) {
            this.savingUuid = assetLocation.uuid;
            this.formError = null;

            try {
                await this.$http.post(
                    `/lite-integrations/${this.liteIntegration.uuid}/asset-locations`,
                    {
                        asset_location_uuid:
                            assetLocation.uuid,
                    }
                );

                await Promise.all([
                    this.loadAssetLocations(),
                    this.loadAvailableAssetLocations(),
                ]);
            } catch (error) {
                this.formError =
                    this.getValidationError(
                        error,
                        'asset_location_uuid'
                    )
                    ?? this.getErrorMessage(
                        error,
                        'Unable to add the asset location.'
                    );
            } finally {
                this.savingUuid = null;
            }
        },

        async removeAssetLocation(relationship) {
            const assetLocation =
                relationship.asset_location;

            const assetLocationName =
                assetLocation && assetLocation.name
                    ? assetLocation.name
                    : 'this asset location';

            const confirmed = window.confirm(
                `Remove "${assetLocationName}" from this Lite Integration?`
            );

            if (!confirmed) {
                return;
            }

            this.deletingUuid = relationship.uuid;
            this.error = null;

            try {
                await this.$http.delete(
                    `/lite-integrations/${this.liteIntegration.uuid}`
                    + `/asset-locations/${relationship.uuid}`
                );

                this.assetLocations =
                    this.assetLocations.filter(
                        item =>
                            item.uuid !== relationship.uuid
                    );
            } catch (error) {
                this.error = this.getErrorMessage(
                    error,
                    'Unable to remove the asset location.'
                );
            } finally {
                this.deletingUuid = null;
            }
        },

        getExperienceName(experience) {
            return (
                experience.friendly_name
                ?? experience.name
                ?? `Experience ${experience.id}`
            );
        },

        getValidationError(error, field) {
            return (
                error.response
                    ?.data
                    ?.errors
                    ?.[field]
                    ?.[0]
                ?? null
            );
        },

        getErrorMessage(error, fallback) {
            return (
                error.response?.data?.message
                ?? fallback
            );
        },
    },
};
</script>